import {
  artworkUrl,
  formatDuration,
  hasRecording,
  hexColor,
  releaseYear,
  usageLine,
  type CatalogLinkState,
  type CatalogSong,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { useAccess } from '../access/useAccess.ts'
import { useDebounced } from '../people/usePeople.ts'
import { servicesKeys, type Song } from './useServices.ts'

/**
 * A song's recording in Apple Music (docs/15-MUSIC-AND-AI.md): what the song
 * page, the song list and a plan's linked song show, and a Songs
 * Administrator's search, choice, "Not on Apple Music" and "Match
 * automatically". The link itself comes with the song (`song.catalog`) and
 * with each list row (`row.catalog`); these hooks add the rest.
 *
 * Keys live under `servicesKeys.all()`, so anything that refreshes the
 * library refreshes these too.
 */
export const songCatalogKeys = {
  all: () => [...servicesKeys.all(), 'song-catalog'] as const,
  status: () => [...songCatalogKeys.all(), 'status'] as const,
  search: (songId: string, q: string) => [...songCatalogKeys.all(), 'search', songId, q] as const,
  /** What is typed as a new song's title, trimmed and lower case (`typeaheadQuery`). */
  typeahead: (q: string) => [...songCatalogKeys.all(), 'typeahead', q] as const,
  /** A recording chosen for a new song: what it fills in. */
  prefill: (catalogId: string) => [...songCatalogKeys.all(), 'prefill', catalogId] as const,
  discover: () => [...songCatalogKeys.all(), 'discover'] as const,
  playlist: (id: string) => [...songCatalogKeys.all(), 'playlist', id] as const,
}

export type SongCatalog = Song['catalog']
export type CatalogLink = NonNullable<SongCatalog['link']>

/** Is Apple Music set up here, and in which country (the storefront)? */
export function useSongCatalogStatus() {
  const api = useApi()
  const library = useAccess().hasSomewhere('songs', 'viewer')
  return useQuery({
    queryKey: songCatalogKeys.status(),
    enabled: library,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services['song-catalog'].get()
      if (error) throw error
      return data
    },
    staleTime: 5 * 60_000,
  })
}

/** A recording as a header or a row reads it. */
export interface RecordingFacts {
  title: string
  artist: string
  album: string | null
  /** "2019". */
  year: string | null
  /** "4:05". */
  duration: string | null
  /** "Album · 2019 · 4:05": what is known, muted. */
  details: string
  /** Apple's artwork template (`{w}x{h}`), or null. */
  artworkTemplate: string | null
  /** The artwork at a size (CSS px; doubled for sharp screens), or null. */
  artwork: (size: number) => string | null
  /** "#1a2b3c": behind the art while it loads. */
  background: string | null
  previewUrl: string | null
  /** The recording in Apple Music. */
  url: string | null
  explicit: boolean
}

export function recordingFacts(recording: CatalogSong): RecordingFacts {
  const template = recording.artwork?.url ?? null
  const year = releaseYear(recording.releaseDate)
  const duration = recording.durationMs ? formatDuration(recording.durationMs / 1000) : null
  return {
    title: recording.name,
    artist: recording.artistName,
    album: recording.albumName,
    year,
    duration,
    details: [recording.albumName, year, duration].filter(Boolean).join(' · '),
    artworkTemplate: template,
    artwork: (size) => (template ? artworkUrl(template, size * 2) : null),
    background: hexColor(recording.artwork?.bgColor),
    previewUrl: recording.previewUrl,
    url: recording.url,
    explicit: recording.contentRating === 'explicit',
  }
}

/** The state line under a song's recording: what the matcher or a person decided. */
export function catalogStateLine(
  link: Pick<CatalogLink, 'state' | 'decidedBy'> | null,
): string | null {
  if (!link) return 'Looking for it on Apple Music…'
  const lines: Record<CatalogLinkState, string> = {
    matched: 'Matched automatically',
    suggested: 'Suggested match',
    unmatched: 'Not found on Apple Music',
    confirmed: link.decidedBy ? `Chosen by ${link.decidedBy}` : 'Chosen',
    none: 'Not on Apple Music',
  }
  return lines[link.state]
}

/**
 * The song page's Apple Music block: hidden when the feature is off; the
 * recording when there is one; what a Songs Administrator may do.
 */
export function useSongRecording(song: Pick<Song, 'catalog'> | undefined) {
  const admin = useAccess().hasSomewhere('songs', 'administrator')
  const catalog = song?.catalog
  const link = catalog?.link ?? null
  const recording = link && hasRecording(link.state) ? link.recording : null
  return {
    /** Apple Music is set up here: show the block at all. */
    shown: catalog?.enabled === true,
    link,
    facts: recording ? recordingFacts(recording) : null,
    stateLine: catalogStateLine(link),
    /** "Sung by 12 churches this Sunday · 40 this month" (three churches or more), or null. */
    usageLine: recording ? usageLine(link?.usage) : null,
    /** "Churches sing it for": its top themes across churches (three churches each), labels. */
    churchThemes: recording ? (link?.themes ?? []).map((t) => t.label) : [],
    /** A suggestion a Songs Administrator can confirm with one tap. */
    confirmable: admin && link?.state === 'suggested' && recording !== null,
    /** Change match, Not on Apple Music, Match automatically. */
    changeable: admin,
  }
}

/** A Songs Administrator's search (debounced; empty: the song's title and artist). */
export function useCatalogSearch(songId: string, rawQuery: string, enabled = true) {
  const api = useApi()
  const q = useDebounced(rawQuery.trim(), 300)
  const query = useQuery({
    queryKey: songCatalogKeys.search(songId, q),
    enabled: enabled && songId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services
        .songs({ id: songId })
        .catalog.search.get({ query: q ? { q } : {} })
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })
  return {
    ...query,
    /** What was searched (the prefill when nothing is typed). */
    term: query.data?.term ?? '',
    results: (query.data?.results ?? []).map((result) => ({
      ...result,
      facts: recordingFacts(result.recording),
    })),
  }
}

/** Choose a recording, say "Not on Apple Music", or hand it back to the matcher. */
export function useSongCatalogEditor(songId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const put = (catalog: SongCatalog) => {
    queryClient.setQueryData<Song>(servicesKeys.song(songId), (song) =>
      song ? { ...song, catalog } : song,
    )
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'songs'] })
    void queryClient.invalidateQueries({ queryKey: songCatalogKeys.all() })
  }
  const choose = useMutation({
    mutationFn: async (catalogId: string) => {
      const { data, error } = await api.api.v1.services
        .songs({ id: songId })
        .catalog.put({ catalogId })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const none = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.services
        .songs({ id: songId })
        .catalog.put({ none: true })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const automatic = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.services.songs({ id: songId }).catalog.delete()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      put(data)
      // The worker matches it in a moment: look again shortly.
      setTimeout(
        () => void queryClient.invalidateQueries({ queryKey: servicesKeys.song(songId) }),
        3000,
      )
    },
  })
  return { choose, none, automatic }
}
